// Analytics utility for comprehensive user behavior tracking
// Supports Google Analytics 4 (GA4) with custom events
// Designed to fail gracefully when analytics are blocked

// Check if gtag is available and analytics is not blocked
const isAnalyticsAvailable = () => {
  try {
    return typeof window !== 'undefined' && 
           typeof window.gtag === 'function' && 
           window.GA_MEASUREMENT_ID &&
           window.GA_MEASUREMENT_ID !== 'GA_MEASUREMENT_ID'
  } catch (error) {
    return false
  }
}

// Generic event tracking function with comprehensive error handling
const trackEvent = (eventName, parameters = {}) => {
  try {
    if (!isAnalyticsAvailable()) {
      // Debug mode - only log in development
      if (process.env.NODE_ENV === 'development' || window.location.search.includes('dev=hacker')) {
        console.log('Analytics (blocked/unavailable):', eventName, parameters)
      }
      return
    }
    
    window.gtag('event', eventName, {
      event_category: 'user_interaction',
      event_label: parameters.label || '',
      value: parameters.value || 0,
      ...parameters
    })
  } catch (error) {
    // Silently fail - don't break the app if analytics fail
    if (process.env.NODE_ENV === 'development') {
      console.warn('Analytics tracking error (non-critical):', error)
    }
  }
}

// Page view tracking with error handling
export const trackPageView = (pageName, additionalParams = {}) => {
  try {
    if (!isAnalyticsAvailable()) return
    
    window.gtag('config', window.GA_MEASUREMENT_ID, {
      page_title: pageName,
      page_location: window.location.href,
      custom_map: {
        custom_dimension_1: 'user_type',
        custom_dimension_2: 'device_type'
      },
      ...additionalParams
    })
  } catch (error) {
    // Silently fail - analytics should not break the app
    if (process.env.NODE_ENV === 'development') {
      console.warn('Page view tracking error (non-critical):', error)
    }
  }
}

// App lifecycle events
export const analytics = {
  // App initialization
  appLoad: (loadTime) => {
    trackEvent('app_load', {
      event_category: 'app_lifecycle',
      load_time: loadTime,
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    })
  },

  // Navigation tracking
  viewChange: (fromView, toView) => {
    trackEvent('view_change', {
      event_category: 'navigation',
      from_view: fromView,
      to_view: toView,
      timestamp: new Date().toISOString()
    })
  },

  // Session management
  sessionStar: (sessionId, sessionTitle, category) => {
    trackEvent('session_star', {
      event_category: 'session_management',
      session_id: sessionId,
      session_title: sessionTitle,
      session_category: category,
      action: 'star'
    })
  },

  sessionUnstar: (sessionId, sessionTitle, category) => {
    trackEvent('session_unstar', {
      event_category: 'session_management',
      session_id: sessionId,
      session_title: sessionTitle,
      session_category: category,
      action: 'unstar'
    })
  },

  sessionView: (sessionId, sessionTitle, category, viewType) => {
    trackEvent('session_view', {
      event_category: 'content_engagement',
      session_id: sessionId,
      session_title: sessionTitle,
      session_category: category,
      view_type: viewType, // 'expanded', 'timeline', 'visual'
      engagement_depth: 'view'
    })
  },

  sessionCalendarExport: (sessionId, sessionTitle, exportMethod) => {
    trackEvent('calendar_export', {
      event_category: 'session_management',
      session_id: sessionId,
      session_title: sessionTitle,
      export_method: exportMethod, // 'google', 'outlook', 'apple', 'ics'
      action: 'export'
    })
  },

  // Speaker interactions
  speakerView: (speakerName, sessionCount) => {
    trackEvent('speaker_view', {
      event_category: 'content_discovery',
      speaker_name: speakerName,
      speaker_session_count: sessionCount,
      engagement_type: 'speaker_profile'
    })
  },

  // Search and filtering
  filterApply: (filterType, filterValue, resultCount) => {
    trackEvent('filter_apply', {
      event_category: 'content_discovery',
      filter_type: filterType, // 'category', 'content_type', 'speaker'
      filter_value: filterValue,
      result_count: resultCount
    })
  },

  searchPerform: (searchTerm, resultCount) => {
    trackEvent('search', {
      event_category: 'content_discovery',
      search_term: searchTerm,
      result_count: resultCount,
      search_type: 'session_search'
    })
  },

  // Timeline interactions
  timelineZoom: (zoomLevel, zoomType) => {
    trackEvent('timeline_zoom', {
      event_category: 'visual_timeline',
      zoom_level: zoomLevel,
      zoom_type: zoomType, // 'button', 'pinch'
      interaction_type: 'zoom'
    })
  },

  timelineScroll: (scrollPosition, timelineView) => {
    trackEvent('timeline_scroll', {
      event_category: 'visual_timeline',
      scroll_position: scrollPosition,
      timeline_view: timelineView, // 'timeline', 'visual'
      interaction_type: 'navigation'
    })
  },

  timeSlotExpand: (timeSlot, sessionCount) => {
    trackEvent('timeslot_expand', {
      event_category: 'timeline_interaction',
      time_slot: timeSlot,
      parallel_sessions: sessionCount,
      interaction_type: 'expand'
    })
  },

  // User engagement metrics
  sessionDuration: (viewName, duration) => {
    trackEvent('session_duration', {
      event_category: 'engagement',
      view_name: viewName,
      duration_seconds: duration,
      engagement_level: duration > 300 ? 'high' : duration > 60 ? 'medium' : 'low'
    })
  },

  featureUsage: (featureName, usageCount) => {
    trackEvent('feature_usage', {
      event_category: 'feature_adoption',
      feature_name: featureName,
      usage_count: usageCount,
      user_engagement: 'feature_interaction'
    })
  },

  // Error tracking
  errorOccurred: (errorType, errorMessage, context) => {
    trackEvent('error_occurred', {
      event_category: 'app_errors',
      error_type: errorType,
      error_message: errorMessage,
      error_context: context,
      user_agent: navigator.userAgent
    })
  },

  // Performance tracking
  performanceMetric: (metricName, value, context) => {
    trackEvent('performance_metric', {
      event_category: 'app_performance',
      metric_name: metricName,
      metric_value: value,
      performance_context: context
    })
  },

  // Location and venue
  venueMapClick: () => {
    trackEvent('venue_map_click', {
      event_category: 'location_interaction',
      interaction_type: 'external_navigation',
      destination: 'google_maps'
    })
  },

  // PWA events
  pwaInstall: () => {
    trackEvent('pwa_install', {
      event_category: 'app_lifecycle',
      installation_type: 'pwa',
      platform: navigator.platform
    })
  },

  pwaLaunch: (launchMode) => {
    trackEvent('pwa_launch', {
      event_category: 'app_lifecycle',
      launch_mode: launchMode, // 'browser', 'standalone'
      platform: navigator.platform
    })
  },

  // Social sharing (if implemented)
  shareSession: (sessionId, shareMethod) => {
    trackEvent('session_share', {
      event_category: 'social_engagement',
      session_id: sessionId,
      share_method: shareMethod, // 'twitter', 'linkedin', 'copy_link'
      content_type: 'session'
    })
  },

  // User preferences
  preferenceChange: (preferenceType, newValue) => {
    trackEvent('preference_change', {
      event_category: 'user_preferences',
      preference_type: preferenceType,
      preference_value: newValue,
      customization_level: 'user_setting'
    })
  },

  // Conflict management
  scheduleConflict: (conflictType, sessionCount) => {
    trackEvent('schedule_conflict', {
      event_category: 'scheduling',
      conflict_type: conflictType, // 'time_overlap', 'venue_distance'
      conflicting_sessions: sessionCount,
      planning_challenge: 'conflict_detection'
    })
  }
}

// Enhanced ecommerce tracking (for future premium features)
export const ecommerce = {
  purchaseEvent: (transactionId, value, currency, items) => {
    if (!isAnalyticsAvailable()) return
    
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items
    })
  }
}

// User identification with error handling
export const identifyUser = (userId, userProperties = {}) => {
  try {
    if (!isAnalyticsAvailable()) return
    
    window.gtag('config', window.GA_MEASUREMENT_ID, {
      user_id: userId,
      custom_map: userProperties
    })
  } catch (error) {
    // Silently fail - don't break the app
    if (process.env.NODE_ENV === 'development') {
      console.warn('User identification error (non-critical):', error)
    }
  }
}

// Debug mode for development with error handling
export const enableDebugMode = () => {
  try {
    if (!isAnalyticsAvailable()) return
    
    window.gtag('config', window.GA_MEASUREMENT_ID, {
      debug_mode: true
    })
  } catch (error) {
    // Silently fail - don't break the app
    if (process.env.NODE_ENV === 'development') {
      console.warn('Debug mode enable error (non-critical):', error)
    }
  }
}

// Test analytics availability (useful for debugging)
export const testAnalyticsAvailability = () => {
  const available = isAnalyticsAvailable()
  const status = {
    available,
    measurementId: window.GA_MEASUREMENT_ID || 'Not configured',
    gtagExists: typeof window.gtag === 'function',
    reasons: []
  }
  
  if (!available) {
    if (!window.GA_MEASUREMENT_ID) status.reasons.push('No measurement ID configured')
    if (window.GA_MEASUREMENT_ID === 'GA_MEASUREMENT_ID') status.reasons.push('Default placeholder ID not replaced')
    if (typeof window.gtag !== 'function') status.reasons.push('Google Analytics script blocked or failed to load')
  }
  
  return status
}

// Initialize analytics availability check on import
if (typeof window !== 'undefined') {
  // Check analytics after a short delay to allow GA script to load
  setTimeout(() => {
    const status = testAnalyticsAvailability()
    if (process.env.NODE_ENV === 'development' || window.location.search.includes('dev=hacker')) {
      console.log('Analytics Status:', status)
    }
  }, 1000)
}

export default analytics 